/**
 * frontendエントリポイント(Phase「LIFF frontend実装」)。
 *
 * 起動フロー(正本 実装指示書 v2.0 11章「LIFF認証」に準拠):
 * 1. liff.init({liffId})
 * 2. 未ログインならliff.login()でLINEログインへ誘導(ここではLIFF側の
 *    セッションのみを扱い、backendのLINE ID Token検証より前に本人確認情報を
 *    確定させない)
 * 3. liff.getIDToken()でID tokenを取得
 * 4. 以降のAPI呼び出しは常にこのID tokenをAuthorizationヘッダへ付与する
 *    (api-client.ts)。ブラウザ側でデコードしたuser_id等は一切使わない
 *    (Workers側の既存LINE ID Token verify、src/auth/line-auth.tsが唯一の
 *    本人確認の正本)。
 */

import "./style.css";
import { loadConfigFromViteEnv } from "./config.js";
import { RealLiffClient, type LiffClient } from "./liff-client.js";
import { RentalBookingApiClient } from "./api-client.js";
import { RealStripeClient } from "./stripe-client.js";
import type { AppContext, ScreenName } from "./app-context.js";
import { renderMyBookingsScreen } from "./screens/my-bookings.js";
import { renderNewBookingScreen } from "./screens/new-booking.js";

/**
 * LIFF navigation診断(iPhone LINE実機で報告されている「読み込み中…→
 * 読み込み中…→予約カレンダー」という多段階表示の実態を、推測ではなく
 * document navigationの証拠で確定するための計測)。
 *
 * 診断専用の副作用であり、予約ロジック・画面遷移ロジック自体には一切
 * 影響しない(すべてtry/catchで囲み、失敗しても本来のbootstrap()を
 * 止めない)。
 *
 * セキュリティ上の制約(記録しないもの):
 * LINE ID token、LINE user ID、Stripe情報、Secret、Authorization header、
 * Cookie、liff.stateの内容そのもの、URLの機密query値。
 * 記録するのは hasLiffState のような真偽値と、"screen"のような既知の
 * 安全なquery値・normalizeされた情報のみ。
 *
 * 保存先はsessionStorageではなくlocalStorageにしている。LINEアプリ内
 * WebViewでliff.login()等の遷移がタブ内遷移ではなく別のWebView
 * (=別のtop-level browsing context)を生成している場合、sessionStorageは
 * 引き継がれない可能性があるが、localStorageは同一origin・同一端末上の
 * ブラウザストレージであれば別WebViewインスタンス間でも共有される。
 * 「documentをまたいでログが引き継がれるか」自体も重要な証拠になる。
 */
const NAV_DIAG_KEY = "rental-booking:liff-nav-diag";
const NAV_DIAG_MAX_ENTRIES = 60;
const navPageId = crypto.randomUUID?.() ?? `${Date.now()}-${Math.random().toString(16).slice(2)}`;

interface NavDiagEntry {
  pageId: string;
  seq: number;
  stage: string;
  detail?: string;
  pathname: string;
  screenParam: string | null;
  hasLiffState: boolean;
  hasOtherQueryParams: boolean;
  referrerHost: string | null;
  navigationType: string;
  visibilityState: DocumentVisibilityState;
  timeOrigin: number;
  now: number;
}

/** "screen"以外の既知でないquery keyが存在するかどうかだけを真偽値で返す(値自体は保持しない)。 */
function safeQueryFlags(url: URL): { screenParam: string | null; hasLiffState: boolean; hasOtherQueryParams: boolean } {
  const KNOWN_SAFE_KEYS = new Set(["screen"]);
  let hasOtherQueryParams = false;
  for (const key of url.searchParams.keys()) {
    if (key === "liff.state") continue;
    if (!KNOWN_SAFE_KEYS.has(key)) {
      hasOtherQueryParams = true;
      break;
    }
  }
  return {
    screenParam: url.searchParams.get("screen"),
    hasLiffState: url.searchParams.has("liff.state"),
    hasOtherQueryParams,
  };
}

/** document.referrerのhostnameのみを返す(path/queryは保持しない)。 */
function safeReferrerHost(): string | null {
  if (!document.referrer) return null;
  try {
    return new URL(document.referrer).hostname;
  } catch {
    return null;
  }
}

function readNavDiagLog(): NavDiagEntry[] {
  try {
    return JSON.parse(localStorage.getItem(NAV_DIAG_KEY) ?? "[]") as NavDiagEntry[];
  } catch {
    return [];
  }
}

function navDiagnostic(stage: string, detail?: string): void {
  try {
    const nav = performance.getEntriesByType("navigation")[0] as PerformanceNavigationTiming | undefined;
    const url = new URL(window.location.href);
    const { screenParam, hasLiffState, hasOtherQueryParams } = safeQueryFlags(url);
    const previous = readNavDiagLog();
    const entry: NavDiagEntry = {
      pageId: navPageId,
      seq: previous.length,
      stage,
      ...(detail ? { detail } : {}),
      pathname: url.pathname,
      screenParam,
      hasLiffState,
      hasOtherQueryParams,
      referrerHost: safeReferrerHost(),
      navigationType: nav?.type ?? "unknown",
      visibilityState: document.visibilityState,
      timeOrigin: performance.timeOrigin,
      now: performance.now(),
    };
    localStorage.setItem(NAV_DIAG_KEY, JSON.stringify([...previous.slice(-(NAV_DIAG_MAX_ENTRIES - 1)), entry]));
    console.info("[LIFF_NAV_DIAG]", entry);
  } catch (error) {
    console.info("[LIFF_NAV_DIAG]", { stage, diagnosticError: String(error) });
  }
}

window.addEventListener("pageshow", (event) => {
  navDiagnostic("window:pageshow", `persisted=${event.persisted}`);
});
window.addEventListener("pagehide", (event) => {
  navDiagnostic("window:pagehide", `persisted=${event.persisted}`);
});
document.addEventListener("visibilitychange", () => {
  navDiagnostic("document:visibilitychange", document.visibilityState);
});

function escapeForDiagDisplay(text: string): string {
  return text.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
}

/** documentId(pageId)ごとにグルーピングし、生成順・経過時間つきの人間可読サマリーを作る。 */
function buildNavDiagSummaryText(): string {
  const log = readNavDiagLog();
  if (log.length === 0) return "診断ログはまだありません。";

  const docOrder: string[] = [];
  const byDoc = new Map<string, NavDiagEntry[]>();
  for (const entry of log) {
    if (!byDoc.has(entry.pageId)) {
      byDoc.set(entry.pageId, []);
      docOrder.push(entry.pageId);
    }
    byDoc.get(entry.pageId)?.push(entry);
  }

  const lines: string[] = [];
  lines.push(`document数: ${docOrder.length}件(pageId単位、生成順に表示)`);
  lines.push("");
  docOrder.forEach((pageId, index) => {
    const entries = byDoc.get(pageId) ?? [];
    const firstTime = entries[0]?.now ?? 0;
    lines.push(`── Document ${index + 1} (pageId: ${pageId.slice(0, 8)}…) ──`);
    for (const entry of entries) {
      const elapsedMs = Math.round(entry.now - firstTime);
      const fields = [
        `+${elapsedMs}ms`,
        entry.stage,
        entry.detail ? `(${entry.detail})` : "",
        `nav=${entry.navigationType}`,
        `vis=${entry.visibilityState}`,
        entry.screenParam ? `screen=${entry.screenParam}` : "",
        `liff.state=${entry.hasLiffState ? "有" : "無"}`,
        entry.hasOtherQueryParams ? "他param=有" : "",
        `referrer=${entry.referrerHost ?? "無"}`,
      ];
      lines.push(fields.filter(Boolean).join(" / "));
    }
    lines.push("");
  });
  return lines.join("\n");
}

const NAV_DIAG_OVERLAY_ID = "liff-nav-diag-overlay";

function closeNavDiagOverlay(): void {
  document.getElementById(NAV_DIAG_OVERLAY_ID)?.remove();
}

/**
 * 診断ログをその場で画面に表示するオーバーレイ(Safari Web Inspectorや
 * PC接続を必要とせず、iPhone上のタップだけで確認・コピーできるように
 * するため)。#appの中身には一切触れず、document.bodyの子として独立に
 * 追加/削除するため、通常の予約画面の状態を壊さない。
 */
function openNavDiagOverlay(): void {
  try {
    closeNavDiagOverlay();
    const overlay = document.createElement("div");
    overlay.id = NAV_DIAG_OVERLAY_ID;
    overlay.setAttribute(
      "style",
      "position:fixed;inset:0;background:#fff;z-index:2147483647;overflow:auto;padding:12px;font-family:-apple-system,sans-serif;font-size:12px;color:#111;",
    );

    const summary = buildNavDiagSummaryText();
    const rawJson = localStorage.getItem(NAV_DIAG_KEY) ?? "[]";
    const buttonStyle = "width:auto;display:inline-block;margin-left:6px;padding:6px 10px;font-size:11px;border-radius:6px;background:#eee;color:#111;border:1px solid #ccc;";

    overlay.innerHTML = `
      <div style="display:flex;justify-content:space-between;align-items:center;margin-bottom:8px;">
        <strong>LIFF診断ログ</strong>
        <div>
          <button type="button" id="liff-nav-diag-copy" style="${buttonStyle}">コピー</button>
          <button type="button" id="liff-nav-diag-clear" style="${buttonStyle}">クリア</button>
          <button type="button" id="liff-nav-diag-close" style="${buttonStyle}">閉じる</button>
        </div>
      </div>
      <p id="liff-nav-diag-copy-status" style="color:#067a3c;min-height:1.2em;"></p>
      <pre style="white-space:pre-wrap;word-break:break-all;background:#f7f7f7;padding:8px;border-radius:8px;">${escapeForDiagDisplay(summary)}</pre>
      <p style="margin-top:12px;">生データ(コピー用、下の欄を長押しして「全選択」→「コピー」も可能):</p>
      <textarea readonly rows="8" style="width:100%;font-family:monospace;font-size:10px;">${escapeForDiagDisplay(rawJson)}</textarea>
    `;

    document.body.appendChild(overlay);

    document.getElementById("liff-nav-diag-close")?.addEventListener("click", closeNavDiagOverlay);
    document.getElementById("liff-nav-diag-clear")?.addEventListener("click", () => {
      try {
        localStorage.removeItem(NAV_DIAG_KEY);
      } catch {
        // クリアに失敗しても表示を再描画するだけで、アプリ本来の動作には影響しない。
      }
      openNavDiagOverlay();
    });
    document.getElementById("liff-nav-diag-copy")?.addEventListener("click", () => {
      const statusEl = document.getElementById("liff-nav-diag-copy-status");
      const clipboard = navigator.clipboard;
      if (!clipboard) {
        if (statusEl) statusEl.textContent = "自動コピー機能が使えません。下の欄を長押しして「全選択」→「コピー」してください。";
        return;
      }
      clipboard
        .writeText(rawJson)
        .then(() => {
          if (statusEl) statusEl.textContent = "コピーしました。";
        })
        .catch(() => {
          if (statusEl) statusEl.textContent = "自動コピーできませんでした。下の欄を長押しして「全選択」→「コピー」してください。";
        });
    });
  } catch {
    // 診断ビューア自体の失敗が本来のアプリ動作へ波及しないようにする。
  }
}

const NAV_DIAG_TRIGGER_ID = "liff-nav-diag-trigger";
const NAV_DIAG_QUERY_KEY = "diag";
const NAV_DIAG_QUERY_VALUE = "1";

/**
 * 診断ログトリガーの表示条件(2026-09-26追加指示「通常利用者の画面には
 * 診断UIを出さない」)。URLクエリパラメータ ?diag=1 が付与されている場合
 * のみtrueを返す。通常利用者はこのパラメータを付けずにLIFFを開くため
 * ボタンは表示されず、将来の実機調査が必要になった場合はURLへ`?diag=1`
 * を付けて開くことで再度有効化できる(診断コード・localStorageログ・
 * navDiagnostic()の記録処理自体は削除せず、表示条件だけを絞る)。
 */
function shouldShowNavDiagTrigger(): boolean {
  try {
    return new URLSearchParams(window.location.search).get(NAV_DIAG_QUERY_KEY) === NAV_DIAG_QUERY_VALUE;
  } catch {
    return false;
  }
}

/**
 * 診断ログ表示トリガー(shouldShowNavDiagTrigger()がtrueのときだけ右下に
 * 小さく表示、実機での1タップで開ける)。index.htmlの静的「読み込み中…」の
 * 段階も含め、bootstrap()の完了を待たずモジュール読み込み時点で即座に
 * 追加する(liff.init()が止まっているdocumentでも診断ログを開けるように
 * するため)。
 */
function mountNavDiagTrigger(): void {
  try {
    if (document.getElementById(NAV_DIAG_TRIGGER_ID)) return;
    const button = document.createElement("button");
    button.id = NAV_DIAG_TRIGGER_ID;
    button.type = "button";
    button.textContent = "診断ログ";
    button.setAttribute(
      "style",
      "position:fixed;right:8px;bottom:8px;z-index:2147483647;opacity:0.55;font-size:10px;line-height:1;padding:6px 10px;border-radius:999px;background:#333;color:#fff;border:none;width:auto;",
    );
    button.addEventListener("click", openNavDiagOverlay);
    document.body.appendChild(button);
  } catch {
    // 診断UIの追加に失敗しても本来のアプリ動作には一切影響させない。
  }
}

async function bootstrap(liff: LiffClient): Promise<void> {
  navDiagnostic("bootstrap:start");
  const appEl = document.getElementById("app");
  if (!appEl) throw new Error("#app element not found");

  const config = loadConfigFromViteEnv();

  try {
    navDiagnostic("liff:init:before");
    await liff.init(config.liffId);
    navDiagnostic("liff:init:after");
  } catch {
    navDiagnostic("liff:init:failed");
    appEl.innerHTML = '<p class="error">LIFFの初期化に失敗しました。LINEアプリ内でこのページを開き直してください。</p>';
    return;
  }

  if (!liff.isLoggedIn()) {
    // login()はLINEログイン画面へ遷移する(戻り後にliffが自動的に
    // ログイン状態で再初期化される、LIFF SDK標準の挙動)。
    navDiagnostic("liff:login:redirect");
    appEl.innerHTML = '<p class="loading">LINEログインへ移動します…</p>';
    liff.login();
    return;
  }

  const idToken = liff.getIDToken();
  if (!idToken) {
    navDiagnostic("liff:idtoken:missing");
    appEl.innerHTML = '<p class="error">LINEログイン情報を取得できませんでした。ページを再読み込みしてください。</p>';
    return;
  }

  // 表記統一(Phase 11 Part I): LINEリッチメニュー・LIFF内の両方で
  // 「予約する」「予約を確認する」に統一する(内部のScreenName/data-tab値
  // "new-booking"/"my-bookings"自体は変更しない)。
  appEl.innerHTML = `
    <nav class="tabs">
      <button type="button" data-tab="my-bookings" class="active">予約を確認する</button>
      <button type="button" data-tab="new-booking" class="inactive">予約する</button>
    </nav>
    <main id="screen-root"></main>
  `;

  const api = new RentalBookingApiClient({
    baseUrl: config.apiBaseUrl,
    // liff.getIDToken()は呼び出しのたびに現在のセッションの値を返す
    // (このクロージャで都度取得し直すことで、長時間タブを開いたままの
    // ケースでもトークンの取り直しが効く構造にしている)。
    getIdToken: () => liff.getIDToken(),
  });

  const screenRoot = document.getElementById("screen-root") as HTMLElement;

  const ctx: AppContext = {
    api,
    config,
    createStripeClient: () => new RealStripeClient(config.stripePublishableKey),
    navigate: (screen: ScreenName) => showScreen(screen),
    openExternal: (url: string) => liff.openWindow(url, true),
  };

  function showScreen(screen: ScreenName): void {
    document.querySelectorAll<HTMLButtonElement>("[data-tab]").forEach((btn) => {
      btn.classList.toggle("active", btn.dataset.tab === screen);
      btn.classList.toggle("inactive", btn.dataset.tab !== screen);
    });
    if (screen === "my-bookings") {
      void renderMyBookingsScreen(screenRoot, ctx);
    } else {
      renderNewBookingScreen(screenRoot, ctx);
    }
  }

  document.querySelectorAll<HTMLButtonElement>("[data-tab]").forEach((btn) => {
    btn.addEventListener("click", () => showScreen(btn.dataset.tab as ScreenName));
  });

  const initialScreen = initialScreenFromQuery();
  navDiagnostic("screen:before-initial", initialScreen);
  showScreen(initialScreen);
  navDiagnostic("screen:after-initial", initialScreen);
}

/**
 * LINEリッチメニュー導線(Phase 11 Part M)。「予約する」ボタンから
 * `?screen=new-booking`付きのLIFF URLへ遷移させることで初期表示を
 * 新規予約タブにできるようにする(「予約を確認する」はパラメータなしの
 * 既定表示のままでよい)。実際のリッチメニューURL自体の確定・作成は
 * このタスクの対象外(LINE公式アカウント側の外部設定)。
 */
function initialScreenFromQuery(): ScreenName {
  try {
    const params = new URLSearchParams(window.location.search);
    return params.get("screen") === "new-booking" ? "new-booking" : "my-bookings";
  } catch {
    return "my-bookings";
  }
}

const ADMIN_PATH = "/admin";

/**
 * Pagesの`/admin`だけLIFF bootstrapを通さず、既存Backend Worker自身の
 * `/admin`(buildAdminUiHtml、src/http/admin-ui-page.ts。既に稼働している
 * 管理画面で、新しい管理画面は作らない)へ直接接続する(2026-09-27追加
 * 指示「MIN-PATHで最小差分、通常のLIFF経路は変更しない」)。
 *
 * 管理者はLINEアプリ内WebViewではなく通常のブラウザで`/admin`を開く想定
 * のため、liff.init()・LINEログイン誘導を一切行わずBackendへリダイレクト
 * するだけに留める(bootstrap()自体を呼ばないため、通常のLIFF起動経路の
 * コードパスには一切影響しない)。
 *
 * `window.location.replace`を使う(history非対応の`.href`代入と異なり、
 * 「読み込み中…」の履歴エントリを残さない)。
 */
function redirectIfAdminPath(): boolean {
  if (window.location.pathname !== ADMIN_PATH) return false;
  try {
    const { apiBaseUrl } = loadConfigFromViteEnv();
    window.location.replace(`${apiBaseUrl}${ADMIN_PATH}`);
  } catch {
    const appEl = document.getElementById("app");
    if (appEl) appEl.innerHTML = '<p class="error">管理画面への接続設定が見つかりません。</p>';
  }
  return true;
}

// jsdom(vitest)環境ではmain.tsを直接importしたテストがトリガーされないよう、
// 実ブラウザのみで自動起動する(テストはbootstrap()を個別にimportして呼ぶ)。
if (typeof window !== "undefined" && typeof document !== "undefined" && !import.meta.env.VITEST) {
  if (!redirectIfAdminPath()) {
    if (shouldShowNavDiagTrigger()) {
      mountNavDiagTrigger();
    }
    void bootstrap(new RealLiffClient());
  }
}

export { bootstrap, shouldShowNavDiagTrigger, redirectIfAdminPath };
